import { DateTime } from "luxon";
import { useMemo } from "react";
import { AccessibleContent } from "../../accessibility";
import { formatDate, formatTimeOfDay } from "../../formatters";
import { TimeIcon } from "../../icons/TimeIcon";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { useLocale } from "../../providers/DesignSystemLocaleContext";
import { Text } from "../../typography";
import type { PermitIntervalType } from "./ParkingOption";
import { ScheduleLabelChip } from "./ScheduleLabelChip";

export const getIntervalType = ({
	startDateTime,
	endDateTime,
	timezone,
}: { startDateTime: string; endDateTime?: string | null; timezone: string }): PermitIntervalType => {
	if (!endDateTime) {
		return "DATE_RANGE";
	}
	const start = DateTime.fromISO(startDateTime).setZone(timezone).startOf("day");
	const end = DateTime.fromISO(endDateTime).setZone(timezone).startOf("day");
	return start.equals(end) ? "SINGLE" : "DATE_RANGE";
};

const DAYS_OF_WEEK = [
	{ key: "MONDAY", label: "Mon", name: "monday" },
	{ key: "TUESDAY", label: "Tue", name: "tuesday" },
	{ key: "WEDNESDAY", label: "Wed", name: "wednesday" },
	{ key: "THURSDAY", label: "Thu", name: "thursday" },
	{ key: "FRIDAY", label: "Fri", name: "friday" },
	{ key: "SATURDAY", label: "Sat", name: "saturday" },
	{ key: "SUNDAY", label: "Sun", name: "sunday" },
] as const;

const DISPLAY_MODE = {
	DATE_LABELS: "dateLabels",
	DAY_LABELS: "dayLabels",
} as const;

type DayOfWeek = (typeof DAYS_OF_WEEK)[number]["key"];

type DateRow = { dateISO: string; label: string; dayKey: DayOfWeek };

type ParkingTimeScheduleProps = {
	interval: {
		type: PermitIntervalType;
		startDateTimeISO: string;
		endDateTimeISO?: string | undefined;
		schedules: {
			startTime: string;
			endTime: string;
			day: DayOfWeek;
		}[];
	};
	timezone: string;
	hideSection?: "days" | "times";
};

export const ParkingTimeSchedule = ({ interval, timezone, hideSection }: ParkingTimeScheduleProps) => {
	const { parkingOption, daysOfWeek } = useDesignSystemI18n();
	const { locale } = useLocale();
	const { schedules, startDateTimeISO, endDateTimeISO } = interval;

	const { displayMode, dateRows } = useMemo(() => {
		const startDay = DateTime.fromISO(startDateTimeISO).setZone(timezone).startOf("day");

		if (!endDateTimeISO) {
			return { displayMode: DISPLAY_MODE.DAY_LABELS, dateRows: [] as DateRow[] };
		}

		const endDay = DateTime.fromISO(endDateTimeISO).setZone(timezone).startOf("day");

		const daysToInclude = Math.floor(endDay.diff(startDay, "days").days) + 1;

		if (daysToInclude > 0 && daysToInclude < 7) {
			const dateRows: DateRow[] = [];
			for (let dayOffset = 0; dayOffset < daysToInclude; dayOffset++) {
				const date = startDay.plus({ days: dayOffset });

				const weekdayEntry = DAYS_OF_WEEK[date.weekday - 1]!;
				const dateISO = date.toISO()!;

				dateRows.push({
					dateISO,
					label: formatDate({
						date: dateISO,
						formatType: "Abbreviated_Date_No_Year",
						timezone,
					}),
					dayKey: weekdayEntry.key,
				});
			}
			return { displayMode: DISPLAY_MODE.DATE_LABELS, dateRows };
		}

		return { displayMode: DISPLAY_MODE.DAY_LABELS, dateRows: [] as DateRow[] };
	}, [startDateTimeISO, endDateTimeISO, timezone]);

	const { allDaysAreEqual, groupedSchedules } = useMemo(() => {
		let groupedSchedules: { [key: string]: { timeRanges: { start: string; end: string }[] } } = {};
		let allDaysAreEqual = true;
		let prevStartTime = undefined;
		let prevEndTime = undefined;

		for (const schedule of schedules) {
			const day = schedule.day.toUpperCase();
			if (!groupedSchedules[day]) {
				groupedSchedules[day] = { timeRanges: [] };
			}

			const currStartTime = schedule.startTime;
			const currEndTime = schedule.endTime;

			if (
				schedules.length !== 7 ||
				(prevStartTime && prevEndTime && (prevStartTime !== currStartTime || prevEndTime !== currEndTime))
			) {
				allDaysAreEqual = false;
			}

			prevStartTime = currStartTime;
			prevEndTime = currEndTime;

			groupedSchedules = {
				...groupedSchedules,
				[day]: {
					timeRanges: [
						...(groupedSchedules[day]?.timeRanges || []),
						{ start: schedule.startTime, end: schedule.endTime },
					],
				},
			};
		}

		return {
			allDaysAreEqual,
			groupedSchedules,
		};
	}, [schedules]);

	// Condense all days into a single row if the schedule less than 7 days and is the same every day
	if (displayMode === DISPLAY_MODE.DAY_LABELS && allDaysAreEqual && Object.keys(groupedSchedules).length > 0) {
		const everyDayRanges = groupedSchedules.MONDAY?.timeRanges;
		const isActiveAllDay = everyDayRanges?.every((range) => range.start === "00:00" && range.end === "23:59");
		const timeLabel = isActiveAllDay
			? parkingOption.allDay
			: everyDayRanges
					?.map(
						(range) =>
							`${formatTimeOfDay({ time: range.start, locale })} - ${formatTimeOfDay({ time: range.end, locale })}`,
					)
					.join(", ");

		if (hideSection === "times") {
			const rangeCount = everyDayRanges?.length ?? 1;
			return (
				<div className="space-y-1">
					<ScheduleLabelChip
						label={parkingOption.everyDay}
						style={{ minHeight: `${rangeCount * 24 + (rangeCount - 1) * 4}px` }}
					/>
				</div>
			);
		}

		if (hideSection === "days") {
			const ranges = everyDayRanges ?? [];
			return (
				<div className="space-y-1">
					{ranges.map((range) => (
						<div key={`${range.start}-${range.end}`} className="flex min-h-6 items-center justify-start">
							{isActiveAllDay ? (
								<Text size="xs">{parkingOption.allDay}</Text>
							) : (
								<div className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1">
									<TimeIcon className="size-3" />
									<Text size="xs">
										{formatTimeOfDay({ time: range.start, locale })} {" - "}
										{formatTimeOfDay({ time: range.end, locale })}
									</Text>
								</div>
							)}
						</div>
					))}
				</div>
			);
		}

		return (
			<AccessibleContent label={`${parkingOption.everyDay}: ${timeLabel}`} As="p">
				<div className="grid grid-cols-[60px_1fr] gap-4" aria-label={`${parkingOption.everyDay} ${timeLabel}`}>
					<ScheduleLabelChip label={parkingOption.everyDay} />

					<div className="flex items-center justify-start">
						<div className="flex flex-col items-start gap-1">
							{isActiveAllDay ? (
								<Text size="xs">{parkingOption.allDay}</Text>
							) : (
								everyDayRanges?.map((range) => (
									<div
										key={`${range.start}-${range.end}`}
										className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1"
									>
										<TimeIcon className="size-3" />
										<Text size="xs">
											{formatTimeOfDay({ time: range.start, locale })} {" - "}
											{formatTimeOfDay({ time: range.end, locale })}
										</Text>
									</div>
								))
							)}
						</div>
					</div>
				</div>
			</AccessibleContent>
		);
	}

	const schedulesToRender =
		displayMode === DISPLAY_MODE.DATE_LABELS
			? dateRows.map((row) => ({
					key: row.dateISO,
					label: row.label,
					screenReaderLabel: formatDate({
						date: row.dateISO,
						formatType: "Abbreviated_Date_No_Year",
						timezone,
						language: locale,
					}),
					daySchedule: groupedSchedules[row.dayKey],
				}))
			: DAYS_OF_WEEK.map((dayOfWeek) => ({
					key: dayOfWeek.key,
					label: dayOfWeek.label,
					screenReaderLabel: daysOfWeek[dayOfWeek.name],
					daySchedule: groupedSchedules[dayOfWeek.key],
				}));

	if (hideSection === "times") {
		const filtered = schedulesToRender.filter(({ daySchedule }) => daySchedule);
		return (
			<div className="space-y-1">
				{filtered.map(({ key, label, daySchedule }) => {
					const rangeCount = daySchedule?.timeRanges?.length ?? 1;
					return (
						<ScheduleLabelChip
							key={key}
							label={label}
							style={{ minHeight: `${rangeCount * 24 + (rangeCount - 1) * 4}px` }}
						/>
					);
				})}
			</div>
		);
	}

	if (hideSection === "days") {
		const flatEntries = schedulesToRender
			.filter(({ daySchedule }) => daySchedule)
			.flatMap(({ key, daySchedule }) => {
				const ranges = daySchedule?.timeRanges ?? [];
				if (ranges.length === 0) {
					return [];
				}
				return ranges.map((range) => ({
					key: `${key}-${range.start}-${range.end}`,
					range,
					isAllDay: range.start === "00:00" && range.end === "23:59",
				}));
			});

		return (
			<div className="space-y-1">
				{flatEntries.map(({ key, range, isAllDay }) => (
					<div key={key} className="flex min-h-6 items-center justify-start">
						{isAllDay ? (
							<Text size="xs">{parkingOption.allDay}</Text>
						) : (
							<div className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1">
								<TimeIcon className="size-3" />
								<Text size="xs">
									{formatTimeOfDay({ time: range.start, locale })} {" - "}
									{formatTimeOfDay({ time: range.end, locale })}
								</Text>
							</div>
						)}
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-1">
			{schedulesToRender
				.filter(({ daySchedule }) => daySchedule)
				.map(({ key, label, screenReaderLabel, daySchedule }) => {
					const isActiveAllDay = daySchedule?.timeRanges?.every(
						(range) => range.start === "00:00" && range.end === "23:59",
					);

					const timeLabel = isActiveAllDay
						? parkingOption.allDay
						: daySchedule?.timeRanges
								?.map(
									(range) =>
										`${formatTimeOfDay({ time: range.start, locale })} - ${formatTimeOfDay({ time: range.end, locale })}`,
								)
								.join(", ");

					return (
						<AccessibleContent key={key} label={`${screenReaderLabel}: ${timeLabel}`} As="p">
							<div className="grid grid-cols-[60px_1fr] gap-4" aria-label={`${label} ${timeLabel}`}>
								<ScheduleLabelChip label={label} />

								<div className="flex items-center justify-start">
									<div className="flex flex-col items-start gap-1">
										{isActiveAllDay ? (
											<Text size="xs">{parkingOption.allDay}</Text>
										) : (
											daySchedule?.timeRanges?.map((range) => (
												<div
													key={`${range.start}-${range.end}`}
													className="flex items-center gap-2 rounded-md bg-neutral-faded px-2 py-1"
												>
													<TimeIcon className="size-3" />
													<Text size="xs">
														{formatTimeOfDay({ time: range.start, locale })} {" - "}
														{formatTimeOfDay({ time: range.end, locale })}
													</Text>
												</div>
											))
										)}
									</div>
								</div>
							</div>
						</AccessibleContent>
					);
				})}
		</div>
	);
};
